import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, limit, orderBy, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import useAuthStore from '../store/useAuthStore';

/**
 * Hook that listens to the user's first (or specified) device
 * and reports online status + telemetry stats.
 *
 * @param {string} [deviceId] – Optional specific device ID. If omitted,
 *   the hook automatically discovers the most-recently-seen device.
 */
export const useDesktopStatus = (deviceId) => {
    const { user } = useAuthStore();
    const [isOnline, setIsOnline] = useState(false);
    const [deviceData, setDeviceData] = useState(null);
    const [stats, setStats] = useState(null);
    const [resolvedDeviceId, setResolvedDeviceId] = useState(deviceId || null);

    // Step 1: Discover device ID if not provided
    useEffect(() => {
        if (deviceId) {
            setResolvedDeviceId(deviceId);
            return;
        }
        if (!user) return;

        // Listen to the devices collection and pick the first device
        const devicesRef = collection(db, 'users', user.uid, 'devices');
        const q = query(devicesRef, limit(5));

        const unsub = onSnapshot(q, (snap) => {
            if (!snap.empty) {
                // Pick the most recently seen device
                let best = null;
                snap.docs.forEach((d) => {
                    const data = d.data();
                    if (!best || (data.last_seen && (!best.data().last_seen || data.last_seen.toDate() > best.data().last_seen.toDate()))) {
                        best = d;
                    }
                });
                if (best) {
                    setResolvedDeviceId(best.id);
                }
            }
        });

        return () => unsub();
    }, [user, deviceId]);

    // Step 2: Listen to the resolved device
    useEffect(() => {
        if (!user || !resolvedDeviceId) {
            setIsOnline(false);
            return;
        }

        let lastSeenTimestamp = null;

        const unsub = onSnapshot(doc(db, 'users', user.uid, 'devices', resolvedDeviceId), (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setDeviceData(data);

                if (data.last_seen) {
                    lastSeenTimestamp = data.last_seen.toDate();
                    setStats(data.telemetry || null);
                    checkOnlineStatus();
                } else {
                    lastSeenTimestamp = null;
                    setIsOnline(false);
                    setStats(null);
                }
            } else {
                setIsOnline(false);
                setStats(null);
            }
        });

        const interval = setInterval(() => {
            checkOnlineStatus();
        }, 5000);

        function checkOnlineStatus() {
            if (!lastSeenTimestamp) {
                setIsOnline(false);
                return;
            }
            const now = new Date();
            const diffSeconds = (now - lastSeenTimestamp) / 1000;
            setIsOnline(diffSeconds < 60);
        }

        return () => {
            unsub();
            clearInterval(interval);
        };
    }, [user, resolvedDeviceId]);

    return { isOnline, deviceData, stats, resolvedDeviceId };
};
